// A panel to pick a time of day (the time picker on PC), built like the
// calendar (calendar.tsx): the same floating panel and colors, with two columns
// for hours and minutes. Phones keep the native input; callers choose.
//
// Promise:
// - Pressing an hour switches to it (minutes kept; 00 when there were none) and
//   the panel stays open. Pressing a minute settles the time and closes it.
// - Minutes come in 5-minute steps. A current minute off the steps is listed too,
//   so it can be picked again without typing.
// - Keys: Up / Down within a column (Home / End to its ends), Left / Right to
//   the other column, Enter / Space picks.
// - Times outside min / max (`"HH:MM"`) cannot be pressed.
// - "Set to now" is the current time in the given time zone, rounded to the step.
import { m } from "@trameria/i18n";
import type { KeyboardEvent, ReactNode } from "react";
import { useLayoutEffect, useRef } from "react";
import { zonedParts } from "../time/format";
import {
  focusWhenShown,
  PICKER_FOOTER_BUTTON,
  PickerPanel,
  type PickerPopoverProps,
} from "./calendar";
import { inRange } from "./calendar-date";

const pad2 = (n: number) => String(n).padStart(2, "0");
const STEP = 5;

/** Splits `"HH:MM"` into hour and minute. Null for another form or out of range. */
export function parseTime(v: string): { h: number; m: number } | null {
  const r = /^(\d{1,2}):(\d{2})/.exec(v);
  if (!r) return null;
  const h = Number(r[1]);
  const mm = Number(r[2]);
  return h < 24 && mm < 60 ? { h, m: mm } : null;
}

export const timeValue = (h: number, mm: number): string =>
  `${pad2(h)}:${pad2(mm)}`;

/** The minutes listed: the 5-minute steps plus the current minute. */
export function minuteOptions(current: number | null): number[] {
  const list = Array.from({ length: 60 / STEP }, (_, i) => i * STEP);
  if (current !== null && !list.includes(current)) {
    list.push(current);
    list.sort((a, b) => a - b);
  }
  return list;
}

/** Now in `timeZone` rounded to the step, kept within the day ("Set to now"). */
export function nowValue(now = new Date(), timeZone?: string): string {
  const p = zonedParts(now, timeZone);
  const total = Math.round((p.hour * 60 + p.minute) / STEP) * STEP;
  const t = Math.min(total, 24 * 60 - STEP);
  return timeValue(Math.floor(t / 60), t % 60);
}

const ITEM =
  "flex h-8 w-full shrink-0 cursor-pointer items-center justify-center rounded-md text-[12.5px] tabular-nums disabled:cursor-default disabled:opacity-30";
/** A column 7 rows high. */
const COLUMN = "flex max-h-[224px] flex-col gap-[2px] overflow-y-auto";
const HEAD = "flex h-6 items-center justify-center text-[11px] text-faint";

export interface TimePickerProps {
  /** The picked time (`"HH:MM"`; empty for none). */
  value: string;
  min?: string;
  max?: string;
  /** The hour changed (the panel stays open). */
  onChange(value: string): void;
  /** The minute was picked too (the panel closes). */
  onSelect(value: string): void;
  autoFocus?: boolean;
  footer?: ReactNode;
  /** IANA time zone for "Set to now" (the account's). Undefined = the browser's. */
  timeZone?: string;
}

export function TimePicker({
  value,
  min,
  max,
  onChange,
  onSelect,
  autoFocus = false,
  footer,
  timeZone,
}: TimePickerProps) {
  const t = parseTime(value);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = minuteOptions(t?.m ?? null);
  const hourCol = useRef<HTMLDivElement>(null);
  const minuteCol = useRef<HTMLDivElement>(null);

  // On open, scroll the picked hour and minute to the middle of their columns
  // and focus the hour.
  // frontend-development: not a fetch; scroll position and focus.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, on open
  useLayoutEffect(() => {
    for (const col of [hourCol.current, minuteCol.current]) {
      const sel = col?.querySelector<HTMLElement>("[aria-selected=true]");
      if (col && sel)
        col.scrollTop =
          sel.offsetTop - col.clientHeight / 2 + sel.offsetHeight / 2;
    }
    if (autoFocus) {
      const col = hourCol.current;
      focusWhenShown(
        col?.querySelector<HTMLElement>("[aria-selected=true]") ??
          col?.querySelector<HTMLElement>("button:not([disabled])"),
      );
    }
  }, []);

  const hourAllowed = (h: number) =>
    minuteOptions(null).some((mm) => inRange(timeValue(h, mm), min, max)) ||
    inRange(timeValue(h, 0), min, max);

  const pickHour = (h: number) => {
    let mm = t?.m ?? 0;
    if (!inRange(timeValue(h, mm), min, max))
      mm =
        minuteOptions(null).find((x) => inRange(timeValue(h, x), min, max)) ??
        mm;
    onChange(timeValue(h, mm));
  };

  // Up / Down within a column, Left / Right between them. Keys rise from the
  // focused button to the wrapper.
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const btn = e.target as HTMLElement;
    const col = btn.closest<HTMLElement>("[data-time-col]");
    if (!col) return;
    const items = [
      ...col.querySelectorAll<HTMLButtonElement>("button:not([disabled])"),
    ];
    const i = items.indexOf(btn as HTMLButtonElement);
    let next: HTMLElement | undefined;
    if (e.key === "ArrowDown") next = items[Math.min(items.length - 1, i + 1)];
    else if (e.key === "ArrowUp") next = items[Math.max(0, i - 1)];
    else if (e.key === "Home") next = items[0];
    else if (e.key === "End") next = items[items.length - 1];
    else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const other =
        col === hourCol.current ? minuteCol.current : hourCol.current;
      next =
        other?.querySelector<HTMLElement>(
          "[aria-selected=true]:not([disabled])",
        ) ??
        other?.querySelector<HTMLElement>("button:not([disabled])") ??
        undefined;
    } else return;
    e.preventDefault();
    next?.focus();
    next?.scrollIntoView?.({ block: "nearest" });
  };

  const now = nowValue(new Date(), timeZone);
  const item = (
    selected: boolean,
    enabled: boolean,
    label: string,
    onClick: () => void,
  ) => (
    <button
      key={label}
      type="button"
      role="option"
      aria-selected={selected}
      tabIndex={selected ? 0 : -1}
      disabled={!enabled}
      onClick={onClick}
      className={`${ITEM} ${
        selected
          ? "bg-accent text-accent-fg"
          : "text-body hover:bg-hover disabled:hover:bg-transparent"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="w-[148px] px-3 py-[6px]">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: only receives keys rising from the option buttons */}
      <div className="grid grid-cols-2 gap-x-2" onKeyDown={onKey}>
        <div className={HEAD}>{m.uifence_time_hour()}</div>
        <div className={HEAD}>{m.uifence_time_minute()}</div>
        <div
          ref={hourCol}
          data-time-col=""
          role="listbox"
          aria-label={m.uifence_time_hour()}
          className={COLUMN}
        >
          {hours.map((h) =>
            item(t?.h === h, hourAllowed(h), pad2(h), () => pickHour(h)),
          )}
        </div>
        <div
          ref={minuteCol}
          data-time-col=""
          role="listbox"
          aria-label={m.uifence_time_minute()}
          className={COLUMN}
        >
          {minutes.map((mm) => {
            const v = timeValue(t?.h ?? 0, mm);
            return item(t?.m === mm, inRange(v, min, max), pad2(mm), () =>
              onSelect(v),
            );
          })}
        </div>
      </div>
      <div className="mt-[4px] flex items-center justify-between border-t border-border pt-[5px]">
        <button
          type="button"
          disabled={!inRange(now, min, max)}
          onClick={() => onSelect(now)}
          className={PICKER_FOOTER_BUTTON}
        >
          {m.uifence_time_now()}
        </button>
        {footer}
      </div>
    </div>
  );
}

/**
 * The time picker in a floating panel (PC), built like CalendarPopover: picking
 * the minute closes the panel and then calls `onChange`; changing only the hour
 * calls `onChange` and leaves it open (the caller passes the new `value` back).
 * `clearLabel` shows a clear button while there is a value.
 */
export function TimePopover({
  open,
  anchor,
  onClose,
  label,
  align,
  value,
  min,
  max,
  clearLabel,
  onChange,
  timeZone,
}: PickerPopoverProps) {
  // Close first (the closer may look at where the focus is), then hand the focus back.
  const finish = (next: string) => {
    onClose();
    anchor.current?.focus({ preventScroll: true });
    if (next !== value) onChange(next);
  };
  return (
    <PickerPanel
      open={open}
      anchor={anchor}
      onClose={onClose}
      label={label}
      align={align}
    >
      <TimePicker
        value={value}
        min={min}
        max={max}
        timeZone={timeZone}
        autoFocus
        onChange={(v) => {
          if (v !== value) onChange(v);
        }}
        onSelect={finish}
        footer={
          clearLabel && value ? (
            <button
              type="button"
              onClick={() => finish("")}
              className={PICKER_FOOTER_BUTTON}
            >
              {clearLabel}
            </button>
          ) : undefined
        }
      />
    </PickerPanel>
  );
}
